import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import { SiAndroid, SiApple } from 'react-icons/si';
import { useLocation } from 'react-router-dom';
import { useDomainType } from '@/hooks/useDomainType';
import { playClickSound } from '@/utils/soundUtils';
import { useTranslation } from 'react-i18next';
import { getTourForPath, useTourGuide } from '@/components/TourGuide';
import IosPwaGuideModal from '@/components/IosPwaGuideModal';

/** Header / accent from `globals.css`; kiosk-matching fallback when the app does not define the variables. */
const SUPPORT_RAIL_TRACK =
  'border-white/12 bg-[linear-gradient(180deg,var(--Background-Header,#040F29)_0%,color-mix(in_srgb,var(--Background-Header,#040F29)_68%,var(--text-high-light,#22d3ee)_32%)_100%)] shadow-[0_18px_35px_rgba(4,15,41,0.5)]';

const SUPPORT_RAIL_ICON_BUTTON_BASE =
  'group relative flex items-center justify-center rounded-xl border border-transparent bg-transparent transition-all duration-200 hover:bg-white/10';

const SUPPORT_RAIL_ICON_BUTTON_ACTIVE =
  'border-[var(--text-high-light,#22d3ee)]/40 bg-white/10 scale-105';

/** Same size as the other rail icons (Si / Lucide). */
const RAIL_BRAND_ICON = 'mx-auto h-7 w-7';

const TelegramIcon = () => (
  <img
    src={`${import.meta.env.BASE_URL}telegram-mark.png`}
    alt=""
    className={`${RAIL_BRAND_ICON} object-contain`}
    draggable={false}
    aria-hidden
  />
);
const TourGuideIcon = () => (
  <HelpCircle className={`${RAIL_BRAND_ICON} text-[var(--text-high-light,#22d3ee)]`} aria-hidden />
);
const AndroidIcon = () => <SiAndroid className={`${RAIL_BRAND_ICON} text-[#3DDC84]`} aria-hidden />;
const AppleIcon = () => <SiApple className={`${RAIL_BRAND_ICON} text-[#f5f5f7]`} aria-hidden />;

type SupportActionType = 'tour_guide' | 'telegram' | 'android_apk' | 'ios_guide';

interface SupportActionItem {
  id: SupportActionType;
  label: string;
  icon: React.ComponentType;
  url?: string;
}

/** Routes where the floating rail is hidden (minimal chrome, full-bleed flows). */
const HIDDEN_ROUTE_MATCHERS = ['/login', '/register', '/account/wallet/deposit-checkout'];

export default function SupportActionRail() {
  const location = useLocation();
  const domainType = useDomainType();
  const { t } = useTranslation();
  const { openWelcome, start, close } = useTourGuide();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [isIosGuideOpen, setIsIosGuideOpen] = useState(false);
  const [isRailExpanded, setIsRailExpanded] = useState(false);

  const actions = useMemo<SupportActionItem[]>(() => {
    const telegramUrl = String(import.meta.env.VITE_CLIENT_TELEGRAM_SUPPORT_URL || '').trim();
    const androidApkUrl = String(import.meta.env.VITE_CLIENT_ANDROID_APK_URL || '').trim();
    const rows: SupportActionItem[] = [
      {
        id: 'telegram',
        label: t('supportRail.telegram.title'),
        icon: TelegramIcon,
        url: telegramUrl,
      },
      {
        id: 'android_apk',
        label: t('supportRail.androidApk.title'),
        icon: AndroidIcon,
        url: androidApkUrl,
      },
      {
        id: 'ios_guide',
        label: t('supportRail.iosGuide.title'),
        icon: AppleIcon,
      },
      {
        id: 'tour_guide',
        label: t('supportRail.tourGuide.title', 'Tour Guide'),
        icon: TourGuideIcon,
      },
    ];
    return rows.filter(
      (item) =>
        item.id === 'tour_guide' ||
        item.id === 'ios_guide' ||
        Boolean(item.url)
    );
  }, [t]);

  const isHiddenRoute = useMemo(
    () => HIDDEN_ROUTE_MATCHERS.some((path) => location.pathname.startsWith(path)),
    [location.pathname]
  );

  const shouldShowRail = domainType === 'mobile' && actions.length > 0 && !isHiddenRoute;

  useEffect(() => {
    if (!shouldShowRail) {
      setIsIosGuideOpen(false);
    }
  }, [shouldShowRail]);

  const closeIosGuide = (source: string) => {
    setIsIosGuideOpen(false);
  };

  if (!shouldShowRail) return null;

  const handleSelectAction = (action: SupportActionItem) => {
    playClickSound();
    if (action.id === 'tour_guide') {
      close();
      const tour = getTourForPath(location.pathname) ?? getTourForPath('/account');
      if (tour) {
        openWelcome(tour.steps);
        start();
      }
      return;
    }
    if (action.id === 'ios_guide') {
      setIsIosGuideOpen(true);
      return;
    }
    if (action.url) {
      window.open(action.url, '_blank', 'noopener,noreferrer');
      if (action.id === 'telegram') {
      } else {
      }
    }
  };

  const renderActionButton = (action: SupportActionItem, index: number, sizeClass: string) => {
    const Icon = action.icon;
    const isActive = action.id === 'ios_guide' && isIosGuideOpen;
    return (
      <li key={action.id}>
        <button
          ref={index === 0 ? triggerRef : undefined}
          type="button"
          onClick={() => handleSelectAction(action)}
          className={`${sizeClass} ${SUPPORT_RAIL_ICON_BUTTON_BASE} ${isActive ? SUPPORT_RAIL_ICON_BUTTON_ACTIVE : ''}`}
          aria-label={action.label}
        >
          <Icon />
        </button>
      </li>
    );
  };

  return (
    <>
      <style>{`
        @keyframes support-rail-slide-in {
          from { opacity: 0; transform: translateX(8px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-[85]">
        <div
          className={`hidden md:block w-[64px] sm:w-[68px] py-3 px-2 rounded-l-[20px] border border-r-0 ${SUPPORT_RAIL_TRACK}`}
        >
          <ul className="flex flex-col gap-2">
            {actions.map((action, index) =>
              renderActionButton(action, index, 'h-[44px] w-[44px] sm:h-[46px] sm:w-[46px]')
            )}
          </ul>
        </div>

        <div className="flex flex-col items-end md:hidden">
          <button
            type="button"
            onClick={() => {
              playClickSound();
              setIsRailExpanded((prev) => !prev);
            }}
            aria-label={isRailExpanded ? t('supportRail.closeMenuAriaLabel') : t('supportRail.openMenuAriaLabel')}
            className={`flex h-[36px] w-[28px] items-center justify-center rounded-l-xl border border-r-0 border-white/15 text-white/90 shadow-[0_8px_24px_rgba(4,15,41,0.45)] transition-all duration-200 hover:brightness-110 ${SUPPORT_RAIL_TRACK}`}
          >
            {isRailExpanded ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>

          {isRailExpanded && (
            <div
              className={`mt-1 w-[64px] py-3 px-2 rounded-l-[20px] border border-r-0 ${SUPPORT_RAIL_TRACK}`}
              style={{ animation: 'support-rail-slide-in 0.18s ease-out' }}
            >
              <ul className="flex flex-col gap-2">
                {actions.map((action, index) =>
                  renderActionButton(action, index, 'h-[44px] w-[44px]')
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      <IosPwaGuideModal
        isOpen={isIosGuideOpen}
        onClose={() => closeIosGuide('close_button')}
      />
    </>
  );
}
